import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  corporationData: {
    info: {},
    pointConfig: {},
    discounts: [],
    pointMethods: { shipping: [], payments: [] },
    products: [],
    categories: [],
  },
  userConfig: {
    loading: false,
    client: {},
    userData: {},
    data: {},
    logged: false,
    language: "es",
    literals: [],
    rol: null,
    token: null,
    componentActive: "login",
    datatable: {
      configuration: {
        maxRows: 10,
        indexStart: 0,
        pages: [0],
        filterBy: { section: "", filter: [] },
      },
      requestQuery: {},
      tables: {},
    },
    alerts: [],
    cart: {
      ShoppingCartProducts: [],
      total: 0,
    },
    carts: [],
    selectedShippingMethodId: null,
    order: {
      orderId: null,
    },
    request: {
      isOrder: false,
      checkoutOrderId: null,
      isRecoveredCart: false,
      products: {},
      payment: {
        total: 0,
        received: 0,
        missing: 0,
        change: 0,
      },
      activePayments: [],
    },
  },
};

const toNumber = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (v == null) return 0;

  // Soporta entradas tipo "10,50" o "10.50"
  const normalized = String(v).replace(",", ".").trim();
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Garantiza la ruta state.userConfig.client.addresses.addresses como array.
 * Evita errores cuando client/address aún no está cargado.
 */
const ensureClientAddressesArray = (state) => {
  if (!state.userConfig.client) state.userConfig.client = {};
  if (!state.userConfig.client.addresses) state.userConfig.client.addresses = {};
  if (!Array.isArray(state.userConfig.client.addresses.addresses)) {
    state.userConfig.client.addresses.addresses = [];
  }
  return state.userConfig.client.addresses.addresses;
};

const normalizeProductItems = (value) => {
  const list = Array.isArray(value) ? value : value == null || value === "" ? [] : [value];
  return list.map(String).filter(Boolean).sort().join(",");
};

const getProductId = (item) =>
  item?.productId ?? item?.Product?.productId ?? item?.Product?.id ?? item?.id ?? null;

const getProductLineKey = (item) =>
  [
    String(getProductId(item) ?? ""),
    String(item?.variantId ?? ""),
    normalizeProductItems(item?.itemIds),
    String(item?.note ?? "").trim(),
  ].join("|");

const getLinePrice = (item) => {
  const quantity = toNumber(item?.quantity);
  const lineTotal = item?.lineTotal ?? item?.subtotal ?? item?.totalPrice;
  if (lineTotal != null && quantity > 0) return toNumber(lineTotal) / quantity;

  const basePrice = toNumber(item?.Product?.price ?? item?.price ?? item?.unitPrice);
  const variantPrice = (item?.variants ?? item?.Product?.variants ?? []).find(
    (variant) => String(variant?.variantId) === String(item?.variantId),
  )?.price;
  const selectedItems = normalizeProductItems(item?.itemIds).split(",").filter(Boolean);
  const itemsPrice = (item?.itemGroups ?? item?.Product?.itemGroups ?? []).reduce(
    (total, group) =>
      total +
      (group?.items ?? []).reduce(
        (subtotal, option) =>
          subtotal + (selectedItems.includes(String(option?.itemId)) ? toNumber(option?.price) : 0),
        0,
      ),
    0,
  );
  return basePrice + toNumber(variantPrice) + itemsPrice;
};

const calculateProductsTotal = (products) =>
  (Array.isArray(products) ? products : []).reduce(
    (total, item) => total + getLinePrice(item) * toNumber(item?.quantity),
    0,
  );

const buildOptimisticCartItem = (source, quantity, shoppingCartId) => {
  const productId = getProductId(source);
  const product = source?.Product ?? {
    ...source,
    productId,
    id: source?.id ?? productId,
  };

  return {
    ...source,
    productId,
    shoppingCartId,
    quantity,
    Product: product,
  };
};

const setOptimisticQuantity = (products, source, quantity, buildItem) => {
  const list = Array.isArray(products) ? products : [];
  const lineKey = getProductLineKey(source);
  const index = list.findIndex((item) => getProductLineKey(item) === lineKey);

  if (quantity <= 0) {
    if (index >= 0) list.splice(index, 1);
    return list;
  }

  if (index >= 0) list[index].quantity = quantity;
  else list.push(buildItem(source, quantity));

  return list;
};

const userConfigSlice = createSlice({
  name: "aplicationConfig",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.userConfig.loading = action.payload;
    },

    setAlert: (state, action) => {
      const id = new Date().getTime();
      state.userConfig.alerts.push({ id, ...action.payload });
    },

    removeAlert: (state, action) => {
      state.userConfig.alerts = state.userConfig.alerts.filter((alert) => alert.id !== action.payload);
    },

    setCorporationData: (state, action) => {
      state.corporationData.info = action.payload;
    },
    updateCorporationData: (state, action) => {
      const { key, data } = action.payload;
      if (key) state.corporationData[key] = data;
    },
    updateCorporationProducts: (state, action) => {
      state.corporationData.products = action.payload;
    },

    updateCorporationCategories: (state, action) => {
      state.corporationData.categories = action.payload;
    },

    updateCorporationPointConfig: (state, action) => {
      state.corporationData.pointConfig = action.payload;
    },

    updatePointDeliveryMethods: (state, action) => {
      state.corporationData.pointMethods.shipping = action.payload;
    },

    updatePointPaymentMethods: (state, action) => {
      state.corporationData.pointMethods.payments = action.payload;
    },

    updateDiscounts: (state, action) => {
      state.corporationData.discounts = action.payload;
    },

    setUserData: (state, action) => {
      state.userConfig.data = action.payload;
    },

    setUserConfigData: (state, action) => {
      state.userConfig.data = action.payload;
    },

    setUserLogged: (state, action) => {
      state.userConfig.logged = action.payload;
    },

    setUserLogOut: (state) => {
      state.userConfig = { ...initialState.userConfig };
    },

    setUserLanguage: (state, action) => {
      state.userConfig.language = action.payload;
    },

    setLanguage: (state, action) => {
      state.userConfig.literals = action.payload;
    },

    setUserRol: (state, action) => {
      state.userConfig.rol = action.payload;
    },

    setToken: (state, action) => {
      state.userConfig.token = action.payload;
      state.userConfig.logged = true;
    },

    setComponentActive: (state, action) => {
      state.userConfig.componentActive = action.payload;
    },

    updateDatatableConfiguration: (state, action) => {
      state.userConfig.datatable.configuration = action.payload;
    },

    updateDatatableFilter: (state, action) => {
      const { section, filter } = action.payload;
      state.userConfig.datatable.configuration.filterBy.section = section;
      state.userConfig.datatable.configuration.filterBy.filter = filter;
    },

    updateDatatableCheckColumn: (state, action) => {
      const { checkColumn = null, table = "default" } = action.payload;

      if (!state.userConfig.datatable.tables[table]) {
        state.userConfig.datatable.tables[table] = {
          checkColumn: false,
          selectedRows: [],
        };
      }

      state.userConfig.datatable.tables[table].checkColumn =
        checkColumn === null ? !state.userConfig.datatable.tables[table].checkColumn : checkColumn;
    },

    updateDatatableSelectedRows: (state, action) => {
      const { selectedRows = [], table = "default" } = action.payload;

      if (!state.userConfig.datatable.tables[table]) {
        state.userConfig.datatable.tables[table] = {
          checkColumn: false,
          selectedRows: [],
        };
      }

      state.userConfig.datatable.tables[table].selectedRows = selectedRows;
    },

    updateDatatableRemovedRows: (state, action) => {
      const { removedRows = [], table = "default" } = action.payload;

      if (state.userConfig.datatable.tables[table]) {
        state.userConfig.datatable.tables[table].selectedRows = state.userConfig.datatable.tables[table].selectedRows.filter(
          (id) => !removedRows.includes(id),
        );
      }
    },

    updateDatatableRequestQuery: (state, action) => {
      state.userConfig.datatable.requestQuery = action.payload;
    },

    updateCart: (state, action) => {
      state.userConfig.cart = action.payload;
    },

    setOptimisticCartProductQuantity: (state, action) => {
      const { source, quantity } = action.payload || {};
      if (!source) return;

      if (!state.userConfig.cart) state.userConfig.cart = {};
      const products = Array.isArray(state.userConfig.cart.ShoppingCartProducts)
        ? state.userConfig.cart.ShoppingCartProducts
        : [];
      state.userConfig.cart.ShoppingCartProducts = setOptimisticQuantity(
        products,
        source,
        toNumber(quantity),
        (item, nextQuantity) => buildOptimisticCartItem(item, nextQuantity, state.userConfig.cart.shoppingCartId),
      );
      state.userConfig.cart.total = calculateProductsTotal(state.userConfig.cart.ShoppingCartProducts);
    },

    setOptimisticOrderProductQuantity: (state, action) => {
      const { source, quantity } = action.payload || {};
      if (!source || !state.userConfig.order) return;

      const products = Array.isArray(state.userConfig.order.products) ? state.userConfig.order.products : [];
      state.userConfig.order.amounts = null;
      state.userConfig.order.products = setOptimisticQuantity(
        products,
        source,
        toNumber(quantity),
        (item, nextQuantity) => ({
          ...item,
          productId: getProductId(item),
          quantity: nextQuantity,
        }),
      );

    },

    updateCartShippingAddresses: (state, action) => {
      const addresses = ensureClientAddressesArray(state);
      addresses.unshift(action.payload);
    },

    changeOrderShippingAddresses: (state, action) => {
      const addressId = action.payload;
      if (!addressId) return;

      if (!state.userConfig.client) state.userConfig.client = {};
      if (!state.userConfig.client.addresses) state.userConfig.client.addresses = {};

      const reorderById = (list) => {
        if (!Array.isArray(list)) return list;
        const index = list.findIndex((address) => String(address?.addressId) === String(addressId));
        if (index > 0) {
          const [movedAddress] = list.splice(index, 1);
          list.unshift(movedAddress);
        }
        return list;
      };

      const addresses = ensureClientAddressesArray(state);
      reorderById(addresses);

      if (Array.isArray(state.userConfig.client.addresses.shippingAddresses)) {
        reorderById(state.userConfig.client.addresses.shippingAddresses);
      }
    },

    reconcileSelectedCart: (state, action) => {
      const {customerId,expectedCartId,cart,carts} = action.payload;
      if (state.userConfig.order?.orderId || state.userConfig.client?.customerId !== customerId || (state.userConfig.cart?.shoppingCartId || null) !== (expectedCartId || null)) return;
      state.userConfig.carts = carts;
      state.userConfig.cart = cart || {customerId,ShoppingCartProducts:[],total:0};
      if (!cart) state.userConfig.request.isRecoveredCart = false;
    },

    updateCarts: (state, action) => {
      state.userConfig.carts = action.payload;

      if (Array.isArray(action.payload) && action.payload.length > 0) {
        const recoveredCartId = state.userConfig.request.isRecoveredCart ? state.userConfig.cart?.shoppingCartId : null;
        const recoveredCart = recoveredCartId
          ? action.payload.find((cart) => String(cart?.shoppingCartId) === String(recoveredCartId))
          : null;

        if (recoveredCart) {
          state.userConfig.cart = recoveredCart;
          return;
        }

        const activeCarts = action.payload.filter((cart) => cart?.state === "ACTIVE");
        if (activeCarts.length > 0) {
          // Obtengo el último carrito activo
          // API orders carts by updatedAt DESC: first is the most recent sale.
          const cart = activeCarts[0];
          state.userConfig.cart = cart;
        }
      }
    },

    selectCustomerCart: (state, action) => {
      const { client, carts, cart } = action.payload;
      // Publish customer and basket together so no reader sees mixed identities.
      state.userConfig.client = client;
      state.userConfig.carts = carts;
      state.userConfig.cart = cart || { customerId: client.customerId, ShoppingCartProducts: [], total: 0, notes: [], address: null };
      state.userConfig.order = { orderId: null };
      state.userConfig.request = { ...initialState.userConfig.request, isRecoveredCart: Boolean(cart?.shoppingCartId) };
    },

    selectParkedSale: (state, action) => {
      const {expectedCartId, expectedCustomerId, client, cart, shippingMethodId} = action.payload;
      if (state.userConfig.order?.orderId || (state.userConfig.cart?.shoppingCartId || null)!==(expectedCartId || null) || state.userConfig.client?.customerId!==expectedCustomerId) return;
      if (!client?.customerId || !shippingMethodId) return;
      state.userConfig.client=client;
      state.userConfig.selectedShippingMethodId=shippingMethodId;
      state.userConfig.cart=cart || {customerId:client.customerId,ShoppingCartProducts:[],total:0,notes:[],address:null};
      state.userConfig.carts=cart?[cart]:[];
      state.userConfig.order={orderId:null};
      state.userConfig.request={...initialState.userConfig.request,isRecoveredCart:Boolean(cart)};
    },

    completeConfirmedSale: (state, action) => {
      const { orderId, client } = action.payload;
      const pickup = state.corporationData.pointMethods.shipping.find(
        method => method.storeDeliveryIsActive && !method.homeDeliveryIsActive,
      );
      if (state.userConfig.order?.orderId !== orderId || !client?.customerId || !pickup) return;
      state.userConfig.client = client;
      state.userConfig.selectedShippingMethodId = pickup.shippingMethodId;
      state.userConfig.cart = { customerId: client.customerId, ShoppingCartProducts: [], total: 0, notes: [], address: null };
      state.userConfig.carts = [];
      state.userConfig.order = { orderId: null };
      state.userConfig.request = { ...initialState.userConfig.request };
      state.userConfig.saleConfirmation = action.payload.showPulse === false ? null : { orderId, number: action.payload.number || 'Pedido' };
    },

    clearSaleConfirmation: (state, action) => {
      if (state.userConfig.saleConfirmation?.orderId === action.payload)
        state.userConfig.saleConfirmation = null;
    },

    updateClient: (state, action) => {
      state.userConfig.client = action.payload;
    },

    updateSelectedShippingMethod: (state, action) => {
      state.userConfig.selectedShippingMethodId = action.payload ?? null;
    },

    updateOrder: (state, action) => {
      state.userConfig.order = action.payload;
    },

    requestOrderCheckout: (state, action) => { state.userConfig.request.checkoutOrderId = action.payload ?? null; },

    updateRequestIsOrder: (state, action) => {
      state.userConfig.request.isOrder = action.payload;
    },

    updateRequestIsRecoveredCart: (state, action) => {
      state.userConfig.request.isRecoveredCart = Boolean(action.payload);
    },


    updateRequestProducts: (state, action) => {
      state.userConfig.request.products = action.payload;
    },

    /**
     * IMPORTANT: siempre guardamos números para evitar NaN y errores con toFixed()
     */
    updateRequestPayments: (state, action) => {
      const total = toNumber(action.payload?.total);
      const received = toNumber(action.payload?.received);
      const missing = toNumber(action.payload?.missing);
      const change = toNumber(action.payload?.change);

      state.userConfig.request.payment = { total, received, missing, change };
    },

    /**
     * IMPORTANT: value siempre numérico.
     * Si el método ya existe, actualiza.
     */
    updateRequestActivePayments: (state, action) => {
      const paymentMethodId = action.payload?.paymentMethodId;
      const value = toNumber(action.payload?.value);

      if (!paymentMethodId) return;

      const existingPayment = state.userConfig.request.activePayments.find(
        (payment) => payment.paymentMethodId === paymentMethodId,
      );

      if (existingPayment) {
        existingPayment.value = value;
      } else {
        state.userConfig.request.activePayments.push({
          paymentMethodId,
          value,
        });
      }
    },

    /**
     * IMPORTANT: evita duplicados.
     * Si no existe, lo añade con value numérico.
     */
    addRequestActivePayments: (state, action) => {
      const paymentMethodId = action.payload?.paymentMethodId;
      if (!paymentMethodId) return;

      const exists = state.userConfig.request.activePayments.some((payment) => payment.paymentMethodId === paymentMethodId);

      if (!exists) {
        state.userConfig.request.activePayments.push({
          paymentMethodId,
          value: toNumber(action.payload?.value),
        });
      }
    },

    initRequestPayments: (state) => {
      state.userConfig.request.isOrder = false;
      state.userConfig.request.products = {};
      state.userConfig.request.activePayments = [];
      state.userConfig.request.payment = {
        total: 0,
        received: 0,
        missing: 0,
        change: 0,
      };
    },
  },
});

export const {
  selectCustomerCart,
  requestOrderCheckout,
  updateRequestIsOrder,
  updateRequestIsRecoveredCart,
  updateRequestProducts,
  updateRequestPayments,
  setCorporationData,
  updateCorporationPointConfig,
  updateCorporationCategories,
  updateCorporationProducts,
  updateRequestActivePayments,
  addRequestActivePayments,
  initRequestPayments,
  setUserData,
  setUserLogged,
  setUserLanguage,
  setLanguage,
  setUserRol,
  setComponentActive,
  updateDatatableConfiguration,
  setToken,
  setUserLogOut,
  setAlert,
  removeAlert,
  updateDatatableFilter,
  updateDatatableCheckColumn,
  updateCart,
  setOptimisticCartProductQuantity,
  setOptimisticOrderProductQuantity,
  updateCartShippingAddresses,
  changeOrderShippingAddresses,
  updateCarts,
  reconcileSelectedCart,
  updateDatatableSelectedRows,
  updateDatatableRemovedRows,
  updateDatatableRequestQuery,
  setUserConfigData,
  updateClient,
  completeConfirmedSale,
  selectParkedSale,
  clearSaleConfirmation,
  updateSelectedShippingMethod,
  updatePointDeliveryMethods,
  updatePointPaymentMethods,
  updateOrder,
  updateDiscounts,
  setLoading,
  updateCorporationData,
} = userConfigSlice.actions;

export default userConfigSlice.reducer;
